import { useId, useRef, useState } from "react";
import { FEATURE_NAME_MAX_LENGTH, FEATURE_SELECTION_MAX_ITEMS, FUNKO_FEATURES, normalizeFeatureName } from "@farnsworth/shared";
import { useCreateFeature, useFeatureSuggestions } from "../hooks/useInventory.js";
import { Button, ErrorBanner, Input, Select } from "./ui.js";

export function FeatureMultiSelect({ values, onChange }: { values: string[]; onChange: (values: string[]) => void }) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const current = useRef(values); current.current = values;
  const suggestions = useFeatureSuggestions(search);
  const create = useCreateFeature();
  const name = normalizeFeatureName(search);
  const all = [...new Map([...FUNKO_FEATURES, ...(suggestions.data?.data ?? []), ...values].map(value => [value.toLowerCase(), value])).values()].sort((a, b) => a.localeCompare(b));
  const visible = all.filter(value => value.toLowerCase().includes(search.trim().toLowerCase()));
  const existing = all.find(value => value.toLowerCase() === name.toLowerCase());
  async function add() {
    if (!name || create.isPending || values.length >= FEATURE_SELECTION_MAX_ITEMS) return;
    setError(null);
    try {
      const saved = existing ?? await create.mutateAsync(name);
      if (!current.current.some(value => value.toLowerCase() === saved.toLowerCase())) onChange([...current.current, saved]);
      setSearch("");
    } catch (failure) { setError((failure as Error).message); }
  }
  return <div className="space-y-2">
    <label htmlFor={id} className="text-xs font-medium uppercase tracking-wide text-slate-400">Features</label>
    <div className="flex gap-2">
      <Input aria-label="Find or add a feature" value={search} maxLength={FEATURE_NAME_MAX_LENGTH} placeholder="Find a feature or type a new name" onChange={event => setSearch(event.target.value)} onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); void add(); } }} />
      <Button type="button" variant="secondary" onClick={() => void add()} disabled={!name || create.isPending || values.length >= FEATURE_SELECTION_MAX_ITEMS}>{create.isPending ? "Adding…" : existing ? "Select" : "Add"}</Button>
    </div>
    <Select id={id} multiple size={6} aria-describedby={`${id}-hint`} value={values} disabled={create.isPending} onChange={event => {
      const chosen = [...event.target.selectedOptions].map(option => option.value);
      const hidden = values.filter(value => !visible.includes(value));
      const next = [...hidden, ...chosen];
      if (next.length > FEATURE_SELECTION_MAX_ITEMS) { setError("Select at most 50 features."); return; }
      setError(null); onChange(next);
    }}>
      {visible.map(value => <option key={value} value={value}>{value}</option>)}
    </Select>
    <p id={`${id}-hint`} className="text-xs text-slate-400">Choose multiple with Ctrl or Command, or find a name and select it. New names are saved to this list.</p>
    {suggestions.data?.hasMore ? <p className="text-xs text-slate-400">Type more to narrow the list.</p> : null}
    {values.length ? <div className="flex flex-wrap gap-2" aria-label="Selected features">{values.map(value => <Button key={value} type="button" variant="secondary" aria-label={`Remove ${value}`} onClick={() => onChange(values.filter(selected => selected !== value))}>{value} ×</Button>)}</div> : null}
    {error ? <ErrorBanner message={error} /> : null}
    {suggestions.isError ? <p role="status" className="text-sm text-amber-400">Could not load saved features. The starting list and current selections are available.</p> : null}
  </div>;
}
